import { theme, type ThemeConfig } from 'antd';
import type { ThemeMode } from '../../stores/settingsStore';

const brand = {
  blue: '#37A6FC',
  navy: '#052E59',
  amber: '#edcf72',
  rose: '#c56972',
};

export function createAppTheme(themeMode: ThemeMode): ThemeConfig {
  const isDark = themeMode === 'dark';

  return {
    cssVar: {},
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: brand.blue,
      colorInfo: brand.blue,
      colorSuccess: '#64884f',
      colorWarning: brand.amber,
      colorLink: isDark ? '#aacbf0' : '#336a9e',
      colorLinkHover: isDark ? '#cfe0f5' : '#275581',
      colorError: brand.rose,
      colorBgLayout: isDark ? '#111315' : '#f7f8f9',
      colorBgContainer: isDark ? '#181b1f' : '#ffffff',
      colorBgElevated: isDark ? '#1d2126' : '#ffffff',
      colorBorder: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(20, 24, 28, 0.16)',
      colorBorderSecondary: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(20, 24, 28, 0.11)',
      colorText: isDark ? '#f1f3f5' : '#20262c',
      colorTextSecondary: isDark ? '#b5bbc2' : '#5b6670',
      colorTextTertiary: isDark ? '#858d96' : '#76808a',
      colorFill: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(20, 24, 28, 0.1)',
      colorFillSecondary: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(20, 24, 28, 0.07)',
      colorFillTertiary: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(20, 24, 28, 0.05)',
      colorFillQuaternary: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(20, 24, 28, 0.03)',
      borderRadius: 8,
      borderRadiusLG: 12,
      controlHeight: 36,
      controlHeightLG: 40,
      fontSize: 14,
      fontSizeHeading1: 30,
      fontSizeHeading2: 24,
      fontSizeHeading3: 20,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      marginXS: 6,
      marginSM: 8,
      margin: 12,
      marginLG: 16,
      marginXL: 20,
      paddingSM: 8,
      padding: 12,
      paddingLG: 16,
      paddingXL: 20,
      motionDurationFast: '0.08s',
      motionDurationMid: '0.12s',
      motionDurationSlow: '0.16s',
      boxShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.24)' : '0 1px 3px rgba(15, 42, 67, 0.07)',
      boxShadowSecondary: isDark ? '0 18px 42px rgba(0, 0, 0, 0.28)' : '0 14px 34px rgba(15, 42, 67, 0.1)',
    },
    components: {
      Button: {
        colorPrimary: isDark ? '#253e52' : '#cfeafe',
        colorPrimaryHover: isDark ? '#2b4c67' : '#bbe1fe',
        colorPrimaryActive: isDark ? '#2b4c67' : '#bbe1fe',
        primaryColor: isDark ? '#f1f3f5' : '#20262c',
        primaryShadow: 'none',
        borderRadius: 8,
        fontWeight: 600,
        contentFontSize: 13,
        contentLineHeight: 1.1,
        paddingInline: 12,
        controlHeight: 32,
        controlHeightLG: 36,
      },
      Card: {
        borderRadiusLG: 12,
        boxShadow: 'none',
        headerFontSize: 14,
        headerFontSizeSM: 13,
        headerHeight: 44,
        headerHeightSM: 38,
        headerPadding: 14,
        headerPaddingSM: 10,
        bodyPadding: 14,
        bodyPaddingSM: 10,
      },
      Input: {
        controlHeight: 36,
        paddingInline: 11,
      },
      Select: {
        controlHeight: 36,
      },
      Menu: {
        itemBorderRadius: 8,
        itemHeight: 42,
      },
      Segmented: {
        trackPadding: 4,
        trackBg: isDark ? '#14171a' : '#e5e9ec',
        itemActiveBg: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(20, 24, 28, 0.07)',
        itemHoverBg: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(20, 24, 28, 0.05)',
        itemHoverColor: isDark ? '#f1f3f5' : '#20262c',
        itemSelectedBg: isDark ? '#24292e' : '#ffffff',
        itemSelectedColor: isDark ? '#f1f3f5' : '#20262c',
      },
      Table: {
        cellFontSize: 13,
        cellFontSizeSM: 12,
        cellPaddingBlock: 7,
        cellPaddingInline: 8,
        cellPaddingBlockSM: 5,
        cellPaddingInlineSM: 7,
        headerBg: isDark ? '#1d2126' : '#edf3f8',
        headerColor: isDark ? '#c8cdd2' : '#20262c',
        rowHoverBg: isDark ? 'rgba(55, 166, 252, 0.08)' : 'rgba(55, 166, 252, 0.08)',
      },
      Drawer: {
        paddingLG: 16,
      },
      Form: {
        itemMarginBottom: 12,
      },
      Tag: {
        borderRadiusSM: 6,
      },
      Tabs: {
        horizontalItemPadding: '8px 10px',
        horizontalItemMargin: '0 10px 0 0',
        cardPadding: '6px 10px',
      },
    },
  };
}
